import logo from '@/assets/images/Growdash FINAL LOGO-02 (1).png';
import BlogPost from '@/components/blog/blog-post';
import NotFound from '@/components/not-found';
import { getBlogPost, getFooterPages } from '@/lib/cms';
import { getLocaleFromRequest } from '@/lib/getLocaleFromRequest';
import type { BlogPostData } from '@/types/blog';
import { Page } from '@growdash/cms-client/generated';
import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import invariant from 'tiny-invariant';

// Define your supported languages (update if needed)

interface LoaderData {
  post: BlogPostData;
  footerPages: Page[];
  locale: string;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  invariant(params.slug, 'slug is required');
  const url = new URL(request.url);

  // Extract language from the URL path. If your URL is structured as e.g. "/en/blog/slug", then the language is the first segment.
  const locale = await getLocaleFromRequest(request);
  const footerPages = await getFooterPages(locale);

  try {
    // Pass the locale to the getBlogPost function
    const post = await getBlogPost(params.slug, locale);
    if (!post) {
      throw new Error('Post not found');
    }
    return json({
      post,
      domain: `${url.protocol}//${url.host}`,
      footerPages,
      locale
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return json({
      post: null,
      domain: '',
      footerPages,
      locale
    });
  }
};

// Generate BlogPosting schema
function generateBlogSchema(post: BlogPostData, domain: string, slug: string) {
  const organization = {
    '@type': 'Organization',
    name: 'Growdash',
    url: domain,
    logo: {
      '@type': 'ImageObject',
      url: `${domain}${logo}`
    }
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    datePublished: post.date,
    dateModified: post.date,
    author: organization,
    publisher: organization,
    ...(post.image && {
      image: {
        '@type': 'ImageObject',
        url: `${domain}${post.image}`
      }
    }),
    description: post.excerpt,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${domain}/blog/${slug}`
    }
  };
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  if (!data?.post) {
    return [
      { title: 'Post Not Found - Growdash Blog' },
      {
        name: 'description',
        content: 'Blog post not found'
      }
    ];
  }

  const { post, domain } = data;
  const title = `Growdash | ${post.title}`;
  const description = post.excerpt;

  return [
    { title },
    { name: 'description', content: description },
    // OpenGraph tags
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'article:published_time', content: post.date },
    ...(post.image ? [{ property: 'og:image', content: post.image }] : []),
    // Schema.org JSON-LD
    {
      'script:ld+json': JSON.stringify(
        generateBlogSchema(post, domain, params.slug!)
      )
    }
  ];
};

export default function BlogPostRoute() {
  const { post, footerPages, locale } = useLoaderData<LoaderData>();

  if (!post) {
    return <NotFound />;
  }

  return <BlogPost post={post} footerPages={footerPages} locale={locale} />;
}
